import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Card, Chip, Divider, FormControl, FormLabel, IconButton, Input, Stack, Typography } from '@mui/joy';
import { FaEnvelope, FaFacebookF, FaInstagram, FaPhoneAlt, FaWhatsapp } from 'react-icons/fa';
import { IconWrapper } from '../components/IconWrapper';
import { directSupabase } from '../lib/supabaseClient';
import { getPublicBaseUrl } from '../utils/newsletterUnsubscribe';

const normalizeEmail = (value: string): string => value.trim().toLowerCase();
const NAVY = '#212d3b';
const GOLD = '#d9a441';

const NewsletterUnsubscribePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialEmail = useMemo(() => searchParams.get('email') || '', [searchParams]);
  const publicBaseUrl = useMemo(() => getPublicBaseUrl(), []);
  const PUBLIC_URL = String(process.env.PUBLIC_URL || '').trim();
  const fallbackLogoSrc = PUBLIC_URL ? `${PUBLIC_URL}/images/brand/keystone-logo.svg` : '/images/brand/keystone-logo.svg';
  const preferredLogoSrc = PUBLIC_URL
    ? `${PUBLIC_URL}/pdf-export-assets/keystone-glazing-document-logo.png`
    : '/pdf-export-assets/keystone-glazing-document-logo.png';
  const [headerLogoSrc, setHeaderLogoSrc] = useState<string>(preferredLogoSrc);
  const siteHref = 'https://keystoneglazing.co.uk/';

  const [email, setEmail] = useState(initialEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleUnsubscribe = async () => {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    setResultMessage(null);

    try {
      const payload = {
        email: normalizedEmail,
        normalized_email: normalizedEmail,
        is_active: true,
        source: 'newsletter_link',
        unsubscribed_at: new Date().toISOString()
      };

      const { error } = await (directSupabase as any)
        .from('newsletter_unsubscribes')
        .insert(payload);

      if (error) {
        // If this email is already present, treat as success so the page remains idempotent.
        if (error.code === '23505') {
          setResultMessage('This email is already unsubscribed from future newsletters.');
          return;
        }
        throw error;
      }

      setResultMessage('You have been unsubscribed from future newsletters.');
    } catch (error) {
      console.error('Failed to unsubscribe from newsletter:', error);
      setErrorMessage('Unable to process your request right now. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    window.location.href = publicBaseUrl;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: 2,
        py: { xs: 2, md: 4 },
        bgcolor: '#f6f8fb'
      }}
    >
      <Stack spacing={2} sx={{ maxWidth: 760, mx: 'auto' }}>
        <Card sx={{ p: 0, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: { xs: 2, md: 2.5 },
              background: `linear-gradient(135deg, ${NAVY} 0%, #2f4053 100%)`,
              color: '#fff'
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {[
                      { href: 'tel:01243302841', label: 'Call 01243 302841', icon: FaPhoneAlt },
                      { href: 'mailto:info@keystoneglazing.co.uk', label: 'Email', icon: FaEnvelope },
                      { href: 'https://www.facebook.com/keystoneglazing/', label: 'Facebook', icon: FaFacebookF },
                      { href: 'https://www.instagram.com/keystoneglazing/?hl=en', label: 'Instagram', icon: FaInstagram },
                      { href: 'https://api.whatsapp.com/send/?phone=447386326666', label: 'WhatsApp', icon: FaWhatsapp }
                    ].map((s) => (
                      <IconButton
                        key={s.label}
                        component="a"
                        href={s.href}
                        target={s.href.startsWith('http') ? '_blank' : undefined}
                        rel={s.href.startsWith('http') ? 'noreferrer' : undefined}
                        size="sm"
                        variant="plain"
                        aria-label={s.label}
                        sx={{
                          color: GOLD,
                          bgcolor: 'transparent',
                          width: 40,
                          height: 40,
                          fontSize: 16,
                          '&:hover': { bgcolor: 'rgba(255, 255, 255)' }
                        }}
                      >
                        <IconWrapper icon={s.icon} />
                      </IconButton>
                    ))}
                  </Stack>

                  <Stack direction="row" spacing={3} alignItems="center">
                    <Box
                      component="img"
                      src={headerLogoSrc}
                      alt="Keystone Glazing"
                      loading="eager"
                      onError={() => {
                        if (headerLogoSrc !== fallbackLogoSrc) setHeaderLogoSrc(fallbackLogoSrc);
                      }}
                      sx={{
                        width: { xs: 180, sm: 300 },
                        height: { xs: 46, sm: 60 },
                        objectFit: 'contain',
                        display: 'block',
                        bgcolor: 'transparent'
                      }}
                    />
                    <Stack spacing={0.25} sx={{ display: { xs: 'none', sm: 'block' } }}>
                      <Typography level="title-md" sx={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'Poppins', fontWeight: 900, lineHeight: 1.1 }}>
                        Newsletter Preferences
                      </Typography>
                      <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'Poppins' }}>
                        Manage your email updates from Keystone Glazing.
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>

              <Stack spacing={0.35} sx={{ textAlign: 'right' }}>
                <Chip
                  variant="solid"
                  sx={{
                    alignSelf: 'flex-end',
                    bgcolor: GOLD,
                    color: '#fff',
                    border: '1px solid rgba(0,0,0,0.08)',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: 0.8
                  }}
                >
                  Newsletter
                </Chip>
                <Typography level="body-sm" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  Preferences Centre
                </Typography>
              </Stack>
            </Stack>
            <Box sx={{ mt: 2, height: 3, borderRadius: 999, bgcolor: GOLD, opacity: 0.95 }} />
          </Box>

          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography level="h2" sx={{ color: NAVY, mb: 1 }}>
              Are you sure you want to unsubscribe?
            </Typography>
            <Typography level="body-md" sx={{ color: 'text.secondary', mb: 2 }}>
              Sorry to see you go. If you unsubscribe, we will stop sending newsletter updates to this email address.
            </Typography>

            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Email address</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@example.com"
              />
            </FormControl>

            {resultMessage && (
              <Alert color="success" sx={{ mb: 2 }}>
                {resultMessage}
              </Alert>
            )}
            {errorMessage && (
              <Alert color="danger" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <Button
                loading={isSubmitting}
                onClick={handleUnsubscribe}
                sx={{ bgcolor: NAVY, '&:hover': { bgcolor: '#1a2431' } }}
              >
                Yes, unsubscribe me
              </Button>
              <Button variant="outlined" color="neutral" onClick={handleCancel}>
                Cancel
              </Button>
            </Stack>
          </Box>
        </Card>

        <Divider />

        <Stack spacing={0.6} sx={{ textAlign: 'center', pb: 1 }}>
          <Typography level="body-sm" sx={{ color: NAVY, fontWeight: 600 }}>
            Built for Strength, Designed for Style.
          </Typography>
          <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.6)' }}>
            © {new Date().getFullYear()} Keystone Glazing ·{' '}
            <Box
              component="a"
              href={siteHref}
              target="_blank"
              rel="noreferrer"
              sx={{ color: NAVY, fontWeight: 800, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {siteHref.replace(/^https?:\/\//, '')}
            </Box>
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

export default NewsletterUnsubscribePage;
